import { Badge } from '@/common/ui/badge'
import { CohortRetentionResponse } from '../../api/services'

interface CohortRetentionHeatmapProps {
	data?: CohortRetentionResponse
	isLoading?: boolean
}

// Helper function to get color intensity based on retention percentage
const getHeatmapColor = (percentage: number): string => {
	if (percentage >= 80) return 'bg-green-600 dark:bg-green-700 text-white'
	if (percentage >= 60) return 'bg-green-500 dark:bg-green-600 text-white'
	if (percentage >= 40)
		return 'bg-yellow-500 dark:bg-yellow-600 text-foreground'
	if (percentage >= 20) return 'bg-orange-500 dark:bg-orange-600 text-white'
	if (percentage >= 10) return 'bg-red-500 dark:bg-red-600 text-white'
	return 'bg-red-600 dark:bg-red-700 text-white'
}

export default function CohortRetentionHeatmapSimplified({
	data,
	isLoading,
}: CohortRetentionHeatmapProps) {
	if (isLoading) {
		return (
			<div className="flex h-64 animate-pulse items-center justify-center rounded bg-muted">
				<div className="text-muted-foreground">กำลังโหลดข้อมูล...</div>
			</div>
		)
	}

	if (!data?.heatmap || data.heatmap.length === 0) {
		return (
			<div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
				<div className="text-center">
					<p className="font-medium text-muted-foreground">ไม่มีข้อมูล</p>
					<p className="mt-1 text-sm text-muted-foreground">
						ไม่พบข้อมูล Cohort Retention ในช่วงเวลาที่เลือก
					</p>
				</div>
			</div>
		)
	}

	const { heatmap, summary } = data

	// Extract retention day keys from first cohort and sort numerically
	const retentionDays =
		heatmap.length > 0
			? Object.keys(heatmap[0].retention).sort((a, b) => {
					const numA = parseInt(a.replace('day', ''))
					const numB = parseInt(b.replace('day', ''))
					return numA - numB
				})
			: []

	return (
		<div className="space-y-4">
			{/* Summary Badges */}
			<div className="flex flex-wrap gap-2">
				<Badge variant="secondary">
					Cohorts: {summary.totalCohorts.toLocaleString()}
				</Badge>
				<Badge variant="outline">
					ผู้เล่นทั้งหมด: {summary.totalPlayers.toLocaleString()}
				</Badge>
				<Badge variant="outline">
					Day 0: {(summary.averageDay0Retention ?? 0).toFixed(1)}%
				</Badge>
				<Badge variant="outline">
					Day 1: {summary.averageDay1Retention.toFixed(1)}%
				</Badge>
				<Badge variant="outline">
					Day 7: {summary.averageDay7Retention.toFixed(1)}%
				</Badge>
				<Badge variant="outline">
					Day 30: {summary.averageDay30Retention.toFixed(1)}%
				</Badge>
			</div>

			{/* Heatmap Table */}
			<div className="overflow-x-auto">
				<div className="min-w-[800px]">
					{/* Header */}
					<div
						className="mb-2 grid gap-1"
						style={{
							gridTemplateColumns: `200px repeat(${retentionDays.length}, 80px)`,
						}}
					>
						<div className="p-2 text-sm font-semibold">Cohort</div>
						{retentionDays.map((day) => (
							<div key={day} className="p-2 text-center text-sm font-semibold">
								{day}
							</div>
						))}
					</div>

					{/* Heatmap Rows */}
					<div className="space-y-1">
						{heatmap.map((cohort) => (
							<div
								key={cohort.cohort}
								className="grid gap-1"
								style={{
									gridTemplateColumns: `200px repeat(${retentionDays.length}, 80px)`,
								}}
							>
								{/* Cohort Label */}
								<div
									className="truncate p-2 text-sm font-medium"
									title={cohort.cohort}
								>
									<div>{cohort.cohort}</div>
									<div className="text-xs text-muted-foreground">
										Size: {cohort.cohortSize.toLocaleString()}
									</div>
								</div>

								{/* Retention Percentages */}
								{retentionDays.map((day) => {
									const percentage = cohort.retention[day] || 0
									return (
										<div
											key={day}
											className={`rounded p-2 text-center text-xs font-medium ${getHeatmapColor(percentage)}`}
											title={`${cohort.cohort} - ${day}: ${percentage.toFixed(1)}%`}
										>
											{percentage.toFixed(1)}%
										</div>
									)
								})}
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Legend */}
			<div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
				<span className="font-semibold">สีแสดงอัตราการกลับมา:</span>
				<div className="flex items-center gap-1">
					<div className="h-4 w-4 rounded bg-green-600 dark:bg-green-700"></div>
					<span>≥80%</span>
				</div>
				<div className="flex items-center gap-1">
					<div className="h-4 w-4 rounded bg-green-500 dark:bg-green-600"></div>
					<span>60-79%</span>
				</div>
				<div className="flex items-center gap-1">
					<div className="h-4 w-4 rounded bg-yellow-500 dark:bg-yellow-600"></div>
					<span>40-59%</span>
				</div>
				<div className="flex items-center gap-1">
					<div className="h-4 w-4 rounded bg-orange-500 dark:bg-orange-600"></div>
					<span>20-39%</span>
				</div>
				<div className="flex items-center gap-1">
					<div className="h-4 w-4 rounded bg-red-500 dark:bg-red-600"></div>
					<span>10-19%</span>
				</div>
				<div className="flex items-center gap-1">
					<div className="h-4 w-4 rounded bg-red-600 dark:bg-red-700"></div>
					<span>&lt;10%</span>
				</div>
			</div>

			{/* Summary Stats */}
			<div className="mt-4 grid grid-cols-2 gap-2 text-sm md:grid-cols-5">
				<div className="text-center">
					<div className="font-semibold text-blue-600 dark:text-blue-400">
						{summary.totalCohorts.toLocaleString()}
					</div>
					<div className="text-muted-foreground">Total Cohorts</div>
				</div>
				<div className="text-center">
					<div className="font-semibold text-purple-600 dark:text-purple-400">
						{(summary.averageDay0Retention ?? 0).toFixed(1)}%
					</div>
					<div className="text-muted-foreground">Avg Day 0</div>
				</div>
				<div className="text-center">
					<div className="font-semibold text-green-600 dark:text-green-400">
						{summary.averageDay1Retention.toFixed(1)}%
					</div>
					<div className="text-muted-foreground">Avg Day 1</div>
				</div>
				<div className="text-center">
					<div className="font-semibold text-yellow-600 dark:text-yellow-400">
						{summary.averageDay7Retention.toFixed(1)}%
					</div>
					<div className="text-muted-foreground">Avg Day 7</div>
				</div>
				<div className="text-center">
					<div className="font-semibold text-red-600 dark:text-red-400">
						{summary.averageDay30Retention.toFixed(1)}%
					</div>
					<div className="text-muted-foreground">Avg Day 30</div>
				</div>
			</div>
		</div>
	)
}
